import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Fuse, { IFuseOptions } from "fuse.js";
import { useEmojiCategories, createJapaneseToEnglishMap } from "../atoms/EmojiCategories";

const Top = () => {
  // 絵文字データを読み込む
  const emojiCategories = useEmojiCategories();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedEmoji, setCopiedEmoji] = useState("");
  const [favorites, setFavorites] = useState<string[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [customs, setCustoms] = useState<Array<{ id: string; emojis: string }>>([]);
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [customEmojis, setCustomEmojis] = useState("");
  const [editingCustomId, setEditingCustomId] = useState<string | null>(null);
  const [editingCustomEmojis, setEditingCustomEmojis] = useState("");
  const customInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const savedFavorites = localStorage.getItem("emoji-favorites");
    if (savedFavorites) {
      try {
        const parsed = JSON.parse(savedFavorites);
        setFavorites(parsed);
      } catch (error) {
        setFavorites([]);
      }
    }

    const savedCustoms = localStorage.getItem("emoji-customs");
    if (savedCustoms) {
      try {
        const parsed = JSON.parse(savedCustoms);
        setCustoms(parsed);
      } catch (error) {
        setCustoms([]);
      }
    }

    setIsInitialized(true);
  }, []);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem("emoji-favorites", JSON.stringify(favorites));
    }
  }, [favorites, isInitialized]);

  useEffect(() => {
    if (isInitialized) {
      localStorage.setItem("emoji-customs", JSON.stringify(customs));
    }
  }, [customs, isInitialized]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsCategoryOpen(false);
      }
    };

    if (isCategoryOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isCategoryOpen]);

  useEffect(() => {
    if (editingCustomId !== null && customInputRef.current) {
      customInputRef.current.focus();
    }
  }, [editingCustomId]);


  type EmojiItem = {
    id: string;
    emoji: string;
    name: string;
    category: string;
    nameNorm: string;
  };

  const normalize = (s: string) =>
    s.toLowerCase().normalize("NFKC").replace(/\s+/g, " ").trim();

  // 全絵文字をフラット化（検索用）
  const allEmojis = useMemo(
    () => emojiCategories.flatMap((category) => category.emojis),
    [emojiCategories]
  );

  // 日本語キーワードを英語に変換するマッピング（EmojiCategoriesから取得）
  const japaneseToEnglishMap = useMemo(() => createJapaneseToEnglishMap(), []);

  // 日本語キーワードを英語に変換
  const translateJapaneseToEnglish = useCallback((query: string): string => {
    const normalized = normalize(query);
    if (japaneseToEnglishMap[normalized]) {
      return japaneseToEnglishMap[normalized];
    }
    for (const [japanese, english] of Object.entries(japaneseToEnglishMap)) {
      if (normalized.includes(japanese) || japanese.includes(normalized)) {
        return english;
      }
    }
    return query;
  }, [japaneseToEnglishMap]);

  const allEmojisWithCategory: EmojiItem[] = useMemo(
    () =>
      emojiCategories.flatMap((cat) =>
        cat.emojis.map((e) => ({
          ...e,
          category: cat.name,
          // 英語名と日本語名の両方で検索できるように正規化
          nameNorm: normalize(e.name + ' ' + (e.japaneseName || '')),
        }))
      ),
    [emojiCategories]
  );

  const threshold = 0.5;
  const fuse = useMemo(() => {
    const options: IFuseOptions<EmojiItem> = {
      keys: [
        { name: "nameNorm", weight: 0.9 },
        { name: "emoji", weight: 0.1 },
      ],
      includeScore: true,
      threshold,
      ignoreLocation: true,
      minMatchCharLength: 1,
    };
    return new Fuse(allEmojisWithCategory, options);
  }, [allEmojisWithCategory, threshold]);

  const filteredEmojis = useMemo(() => {
    const q = searchTerm ?? "";
    if (!q.trim()) {
      return selectedCategory === "all"
        ? allEmojisWithCategory
        : allEmojisWithCategory.filter((e) => e.category === selectedCategory);
    }

    // 日本語を英語に変換
    const translatedQuery = translateJapaneseToEnglish(q);
    // 元のクエリと変換後のクエリの両方で検索
    const searchQueries = [normalize(q), normalize(translatedQuery)].filter(
      (sq) => sq && sq !== q.toLowerCase()
    );
    // 元のクエリも含める
    const allQueries = [normalize(q), ...searchQueries];

    let items: EmojiItem[] = [];
    const seen = new Set<string>();

    // 各クエリで検索
    for (const nq of allQueries) {
      if (!nq) continue;

      const fuseHits = fuse.search(nq).map((r: any) => r.item);

      let directHits: EmojiItem[] = [];
      if (q.length <= 2) {
        directHits = allEmojisWithCategory.filter(
          (e) => e.nameNorm.includes(nq) || e.emoji.includes(q)
        );
      }

      const combined = [...fuseHits, ...directHits];
      for (const item of combined) {
        const key = `${item.emoji}-${item.name}-${item.category}`;
        if (!seen.has(key)) {
          seen.add(key);
          items.push(item);
        }
      }
    }

    return selectedCategory === "all"
      ? items
      : items.filter((e) => e.category === selectedCategory);
  }, [searchTerm, selectedCategory, fuse, allEmojisWithCategory, translateJapaneseToEnglish]);

  const getFavoriteEmojis = () => {
    return allEmojis.filter((item) => favorites.includes(item.emoji));
  };

  const copyToClipboard = async (emoji: string) => {
    try {
      await navigator.clipboard.writeText(emoji);
      setCopiedEmoji(emoji);
      setTimeout(() => setCopiedEmoji(""), 2000);
    } catch (err) {
      console.error("クリップボードへのコピーに失敗しました:", err);
    }
  };

  const addToFavorites = (emoji: string) => {
    setFavorites((prev) => [...prev, emoji]);
  };

  const removeFromFavorites = (emoji: string) => {
    setFavorites((prev) => prev.filter((fav) => fav !== emoji));
  };

  const addCustom = () => {
    if (customEmojis.trim()) {
      const newCustom = {
        id: Date.now().toString(),
        emojis: customEmojis.trim(),
      };
      setCustoms((prev) => [...prev, newCustom]);
      setCustomEmojis("");
      setShowCustomInput(false);
    }
  };

  const removeCustom = (id: string) => {
    setCustoms((prev) => prev.filter((custom) => custom.id !== id));
  };

  const startEditingCustom = (custom: { id: string; emojis: string }) => {
    if (showCustomInput) {
      setShowCustomInput(false);
    }
    setEditingCustomId(custom.id);
    setEditingCustomEmojis(custom.emojis);
  };

  const saveEditedCustom = () => {
    if (editingCustomEmojis.trim()) {
      setCustoms((prev) =>
        prev.map((custom) =>
          custom.id === editingCustomId
            ? { ...custom, emojis: editingCustomEmojis.trim() }
            : custom
        )
      );
      setEditingCustomId(null);
      setEditingCustomEmojis("");
    }
  };

  const copyCustom = async (emojis: string) => {
    try {
      await navigator.clipboard.writeText(emojis);
      setCopiedEmoji(emojis);
      setTimeout(() => setCopiedEmoji(""), 2000);
    } catch (err) {
      console.error("クリップボードへのコピーに失敗しました:", err);
    }
  };

  const handleCancel = () => {
    if (editingCustomId !== null) {
      // 編集中の場合：編集中を解除し、入力欄をクリア
      setEditingCustomId(null);
      setEditingCustomEmojis("");
      setCustomEmojis("");
      if (customInputRef.current) {
        customInputRef.current.focus();
      }
    } else if (customEmojis.trim().length > 0) {
      // 入力欄に文字がある場合：入力欄をクリア
      setCustomEmojis("");
      if (customInputRef.current) {
        customInputRef.current.focus();
      }
    }
  };

  const isCancelButtonEnabled = editingCustomId !== null || customEmojis.trim().length > 0;

  return (
    <div>
      <header className="App-header">
        <h1>😊 絵文字コピー</h1>
        <p>絵文字をクリックしてコピーしましょう！</p>

        <div className="search-container">
          <div className="search-row">
            <div className="custom-select-container" ref={dropdownRef}>
              <div
                className={`custom-select ${isCategoryOpen ? "open" : ""}`}
                onClick={() => setIsCategoryOpen(!isCategoryOpen)}
              >
                <span className="selected-option">
                  {selectedCategory === "all"
                    ? "すべてのカテゴリ"
                    : selectedCategory}
                </span>
                <span className="custom-arrow">▼</span>
              </div>
              {isCategoryOpen && (
                <div className="custom-dropdown">
                  <div
                    className={`dropdown-option ${
                      selectedCategory === "all" ? "selected" : ""
                    }`}
                    onClick={() => {
                      setSelectedCategory("all");
                      setIsCategoryOpen(false);
                    }}
                  >
                    すべてのカテゴリ
                  </div>
                  {emojiCategories.map((category) => (
                    <div
                      key={category.name}
                      className={`dropdown-option ${
                        selectedCategory === category.name ? "selected" : ""
                      }`}
                      onClick={() => {
                        setSelectedCategory(category.name);
                        setIsCategoryOpen(false);
                      }}
                    >
                      {category.name}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <input
              type="text"
              placeholder="AIキーワード検索"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
        </div>

        {favorites.length > 0 && (
          <div className="favorites-section">
            <h2 className="category-title">⭐ お気に入り</h2>
            <div className="favorites-grid">
              {getFavoriteEmojis().map((item, index) => (
                <div key={`fav-${index}`} className="favorite-item">
                  <button
                    id={item.id + "-favorite"}
                    className={`emoji-button ${
                      copiedEmoji === item.emoji ? "copied" : ""
                    }`}
                    onClick={() => copyToClipboard(item.emoji)}
                    title={item.name}
                  >
                    <span className="emoji">{item.emoji}</span>
                  </button>
                  <button
                    className="remove-favorite-btn"
                    onClick={() => removeFromFavorites(item.emoji)}
                    title="削除"
                  >
                    ❌
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div>
          <div className="customs-header">
            <h2 className="category-title">🎨 カスタムセット</h2>
          </div>

          <div className="custom-input-container">
             <input
               ref={customInputRef}
               type="text"
               placeholder="絵文字を入力（例：😊✨🎉）"
               value={
                 editingCustomId !== null ? editingCustomEmojis : customEmojis
               }
               onChange={(e) => {
                 if (editingCustomId !== null) {
                   setEditingCustomEmojis(e.target.value);
                 } else {
                   setCustomEmojis(e.target.value);
                 }
               }}
               className="custom-emojis-input"
             />
             <div className="custom-buttons-container">
               <button
                 className="cancel-custom-btn"
                 onClick={handleCancel}
                 disabled={!isCancelButtonEnabled}
               >
                 キャンセル
               </button>
               <button
                 className="save-custom-btn"
                 onClick={
                   editingCustomId !== null ? saveEditedCustom : addCustom
                 }
                 disabled={
                   editingCustomId !== null
                     ? !editingCustomEmojis.trim()
                     : !customEmojis.trim()
                 }
               >
                 保存
               </button>
             </div>
           </div>

          {customs.length > 0 && (
             <div className="customs-grid">
               {customs.map((custom) => (
                 <div key={custom.id} className="custom-item">
                   <button
                     className={`emoji-button ${
                       editingCustomId === custom.id ? "editing" : ""
                     }`}
                     onClick={() => copyCustom(custom.emojis)}
                     title={custom.emojis}
                     disabled={editingCustomId === custom.id}
                   >
                     <span className="emoji">{custom.emojis}</span>
                     {editingCustomId === custom.id && (
                       <div className="editing-overlay">
                         <span>編集中</span>
                       </div>
                     )}
                   </button>
                   <div className="custom-controls">
                     <button
                       className={`edit-custom-btn ${
                         editingCustomId === custom.id ? "disabled" : ""
                       }`}
                       onClick={() =>
                         startEditingCustom(
                           custom as { id: string; emojis: string }
                         )
                       }
                       title="編集"
                       disabled={editingCustomId === custom.id}
                     >
                       ✏️
                     </button>
                     <button
                       className={`remove-custom-btn ${
                         editingCustomId === custom.id ? "disabled" : ""
                       }`}
                       onClick={() => removeCustom(custom.id)}
                       title="削除"
                       disabled={editingCustomId === custom.id}
                     >
                       ❌
                     </button>
                   </div>
                 </div>
               ))}
             </div>
           )}
        </div>

        {selectedCategory === "all" ? (
          (() => {
            const grouped = new Map<string, EmojiItem[]>();
            for (const e of filteredEmojis) {
              if (!grouped.has(e.category)) grouped.set(e.category, []);
              grouped.get(e.category)!.push(e);
            }

            const sections = emojiCategories
              .map((cat) => {
                const items = grouped.get(cat.name) ?? [];
                if (items.length === 0) return null;
                return (
                  <div key={cat.name} className="category-section">
                    <h2 className="category-title">{cat.name}</h2>
                    <div className="emoji-grid">
                      {items.map((item, index) => (
                        <div
                          key={`${cat.name}-${index}`}
                          className="emoji-item"
                        >
                          <button
                            id={item.id}
                            className={`emoji-button ${
                              copiedEmoji === item.emoji ? "copied" : ""
                            }`}
                            onClick={() => copyToClipboard(item.emoji)}
                            title={item.name}
                          >
                            <span className="emoji">{item.emoji}</span>
                          </button>
                          <button
                            className={`favorite-btn ${
                              favorites.includes(item.emoji) ? "active" : ""
                            }`}
                            onClick={() =>
                              favorites.includes(item.emoji)
                                ? removeFromFavorites(item.emoji)
                                : addToFavorites(item.emoji)
                            }
                            title={
                              favorites.includes(item.emoji)
                                ? "お気に入りから削除"
                                : "お気に入りに追加"
                            }
                          >
                            {favorites.includes(item.emoji) ? "⭐" : "☆"}
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
              .filter(Boolean);

            if (searchTerm && sections.length === 0) {
              return (
                <div className="no-results">
                  <p>
                    「{searchTerm}」に一致する絵文字が見つかりませんでした。
                  </p>
                </div>
              );
            }
            return sections;
          })()
        ) : (
          <>
            <h2 className="category-title">{selectedCategory}</h2>
            <div className="emoji-grid">
              {filteredEmojis.map((item, index) => (
                <div key={index} className="emoji-item">
                  <button
                    id={item.id}
                    className={`emoji-button ${
                      copiedEmoji === item.emoji ? "copied" : ""
                    }`}
                    onClick={() => copyToClipboard(item.emoji)}
                    title={item.name}
                  >
                    <span className="emoji">{item.emoji}</span>
                  </button>
                  <button
                    className={`favorite-btn ${
                      favorites.includes(item.emoji) ? "active" : ""
                    }`}
                    onClick={() =>
                      favorites.includes(item.emoji)
                        ? removeFromFavorites(item.emoji)
                        : addToFavorites(item.emoji)
                    }
                    title={
                      favorites.includes(item.emoji)
                        ? "お気に入りから削除"
                        : "お気に入りに追加"
                    }
                  >
                    {favorites.includes(item.emoji) ? "⭐" : "☆"}
                  </button>
                </div>
              ))}
            </div>
          </>
        )}

        {copiedEmoji && (
          <div className="copy-notification">✅ コピーしました！</div>
        )}
      </header>
      
      {/* 固定位置の寄付カード */}
      {/* <div className="sticky-donation-card">
        <div className="donation-card-content">
          <div className="donation-card-text">
            <span className="donation-emoji">💝</span>
            <span className="donation-message">寄付で応援</span>
          </div>
          <a href="https://comodo-apps.com/donation" className="donation-card-button" target="_blank" rel="noopener noreferrer">
            寄付する
          </a>
        </div>
      </div> */}
    </div>
  );
};

export default Top;
